import type { FC } from 'react';
import { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Typography } from '@mui/material';

import type {
  FilterValue,
  FilterValues,
} from 'src/components/app/context/FilterContext';
import { useFilterContext } from 'src/components/app/context/FilterContext';

import { styles } from './index.styles';

interface FilterChipProps {
  onDeleteFilter: () => void;
  filterName: string;
  renderOr: boolean;
  value: FilterValue;
  pb?: number;

  formatLabel(name: string, values: FilterValues): string;
}

const FilterChip: FC<FilterChipProps> = ({
  onDeleteFilter,
  filterName,
  renderOr,
  value,
  formatLabel,
  pb,
}) => {
  const { t } = useTranslation();
  const { filterGroups, getSpec, removeFilter } = useFilterContext();
  const label = formatLabel(filterName, [value]);

  /**
   * Determine whether a filter can be deleted or not.
   */
  const canDeleteFilter = useMemo(() => {
    const spec = getSpec(filterName);

    if (!spec) return true;

    return !spec.required || filterGroups[filterName].length > 1;
  }, [filterName]);

  /**
   * Handle deletion.
   */
  const handleDelete = useCallback(
    () => (_: any) => {
      removeFilter(filterName, value.label);
      onDeleteFilter();
    },
    [filterName, value.label, onDeleteFilter, removeFilter],
  );

  return (
    <Fragment>
      <Box sx={{ pb: pb }}>
        <Chip
          title={label}
          label={label}
          sx={styles.chip}
          onDelete={canDeleteFilter ? handleDelete() : undefined}
        />
      </Box>
      {renderOr && (
        <Box sx={{ pb: pb, px: 1 }}>
          <Typography
            variant="body1"
            sx={{
              textTransform: 'uppercase',
            }}
          >
            {t('common:or')}
          </Typography>
        </Box>
      )}
    </Fragment>
  );
};

export default FilterChip;
